import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Chip, Drawer, ProgressBar, Skeleton, buttonVariants } from "@heroui/react";
import { ArrowRight, BookOpen, Clock, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDeskMode } from "@/lib/mode";
import { PriceChart } from "../chart/PriceChart";
import type { DailyRiskStatus } from "../daytrader/dayTrader";
import {
	OPEN_SIGNAL_META, STAGE_META, VERDICT_TONE, ago, chartSignalFromTrade, deskCheckFor, deskSetupFor, fmt, ruleCounts, useDesk, useMyTrades,
	volumeText, type DeskRow, type OpenSignal, type Plan, type SummaryPeriod, type Universe,
} from "./deskData";
import { TradeTicket, type TicketRequest } from "./TradeTicket";
import { TodayTrades } from "./TodayTrades";
import type { ChartSignal } from "../chart/chartSignals";
import { AgainstBtcChip, ClosePreviewFor, RuleHelp, StageChip, StatusIcon, VerdictBox } from "./parts";
import { UniverseInfo } from "./UniverseInfo";
import { DirectionChip } from "@/components/kit";

/**
 * Simple desk: the beginner view of the Trade Desk.
 *
 * Built from the Mobbin patterns for beginner crypto apps: one plain answer at
 * the top (Gemini "Basic" / Coinbase simple home), a card per coin instead of
 * a dense table, a step-by-step trade plan in a side drawer, a plain-language
 * daily safety card, and short "learn" cards (Coinbase / PayPal).
 * It reads the same data as the Pro desk (useDesk), so the two never disagree.
 */
export function SimpleDesk() {
	const desk = useDesk();
	const { data, universe, error, loading, scan, countdown, ready, watching, firstLoad, openSignals } = desk;
	const [openSymbol, setOpenSymbol] = useState<string | null>(null);
	const [showUniverse, setShowUniverse] = useState(false);
	// Your own trades: results card, the ticket, and on the coin's chart.
	const [tradesPeriod, setTradesPeriod] = useState<SummaryPeriod>("today");
	const my = useMyTrades(tradesPeriod);
	const [ticket, setTicket] = useState<TicketRequest | null>(null);
	const [ticketToast, setTicketToast] = useState<string | null>(null);
	const drawerTrades = useMemo(
		() => my.trades.filter(t => t.symbol === openSymbol).map(chartSignalFromTrade).filter((s): s is ChartSignal => s !== null),
		[my.trades, openSymbol],
	);
	useEffect(() => {
		if (!ticketToast) return;
		const id = setTimeout(() => setTicketToast(null), 3000);
		return () => clearTimeout(id);
	}, [ticketToast]);

	const rows = useMemo(() => [...ready, ...watching], [ready, watching]);
	const openRow = rows.find(r => r.symbol === openSymbol) ?? null;
	const openSig = openSignals.find(o => o.symbol === openSymbol) ?? null;
	// Live Telegram signals count as ready trades too (one source of truth).
	const liveSignals = openSignals.filter(o => o.state === "valid" && !ready.some(r => r.symbol === o.symbol));
	const otherSignals = openSignals.filter(o => !rows.some(r => r.symbol === o.symbol));
	// Closest to a trade: the first "almost" coin (the backend sorts by stage).
	const closest = watching.find(r => r.verdict.status === "almost") ?? null;

	return (
		<div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
			<div className="flex min-w-0 flex-col gap-5">
				<AnswerHero
					loading={firstLoad}
					coinCount={universe?.included.length ?? 0}
					error={error}
					onRetry={scan}
					ready={ready}
					liveSignals={liveSignals}
					closest={closest}
					countdown={data ? countdown.label : null}
					onOpen={setOpenSymbol}
				/>

				{/* Today's coins show their Telegram signal on their own card
				    (one coin, one answer). This lists only signals for other coins. */}
				{otherSignals.length > 0 && (
					<section aria-labelledby="sent-title">
						<div className="mb-3">
							<h2 id="sent-title" className="text-base font-semibold">Earlier Telegram signals</h2>
							<p className="text-sm text-muted">Still open, but these coins aren't in today's list.</p>
						</div>
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{otherSignals.map(o => <SignalCard key={o.id} sig={o} onOpen={() => setOpenSymbol(o.symbol)} />)}
						</div>
					</section>
				)}

				<section data-tour="coins" aria-labelledby="coins-title">
					<div className="mb-3 flex items-end justify-between gap-3">
						<div>
							<h2 id="coins-title" className="text-base font-semibold">Today's coins</h2>
							<p className="text-sm text-muted">
								The busiest coins today (most traded in 24h) that move enough to day trade. Closest to a trade first.{" "}
								{universe && (
									<button type="button" onClick={() => setShowUniverse(true)} className="text-brand-text underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-focus">
										Why these coins?
									</button>
								)}
							</p>
						</div>
						<Button size="sm" variant="ghost" onPress={scan} isDisabled={loading} aria-label="Check again now">
							<RefreshCw className={cn("size-3.5", loading && "animate-spin")} /> Check again
						</Button>
					</div>

					{firstLoad ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true" aria-label="Checking today's coins">
							{universe
								? universe.included.map(c => <PendingCoinCard key={c.symbol} coin={c} />)
								: Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-36 rounded-card" />)}
						</div>
					) : (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{rows.map(r => <CoinCard key={r.symbol} row={r} onOpen={() => setOpenSymbol(r.symbol)} />)}
						</div>
					)}
				</section>
			</div>

			<aside className="flex flex-col gap-5">
				<SafetyCard />
				<TodayTrades
					my={my}
					period={tradesPeriod}
					onPeriod={setTradesPeriod}
					riskUsd={(desk.settings.accountSize * desk.settings.riskPercent) / 100}
					emptyHint="Open a coin and press Track a trade (practice works too)."
				/>
				<LearnCards />
			</aside>

			<CoinDrawer
				row={openRow}
				sig={openSig}
				onClose={() => setOpenSymbol(null)}
				desk={desk}
				myTrades={drawerTrades}
				// One drawer at a time: the coin drawer closes, the ticket opens.
				onTrack={req => { setOpenSymbol(null); setTicket(req); }}
			/>

			<TradeTicket
				request={ticket}
				price={rows.find(r => r.symbol === ticket?.symbol)?.price ?? openSignals.find(o => o.symbol === ticket?.symbol)?.price ?? null}
				row={rows.find(r => r.symbol === ticket?.symbol) ?? null}
				openSignal={openSignals.find(o => o.symbol === ticket?.symbol) ?? null}
				atr={rows.find(r => r.symbol === ticket?.symbol)?.checklist.levels.atr ?? null}
				onClose={() => setTicket(null)}
				onTracked={setTicketToast}
			/>

			<Drawer.Backdrop isOpen={showUniverse} onOpenChange={setShowUniverse}>
				<Drawer.Content placement="right">
					<Drawer.Dialog className="w-full sm:w-[30rem]">
						<Drawer.CloseTrigger />
						<Drawer.Header><Drawer.Heading>Why these coins?</Drawer.Heading></Drawer.Header>
						<Drawer.Body>{universe && <UniverseInfo universe={universe} />}</Drawer.Body>
						<Drawer.Footer><Button slot="close" variant="secondary">Close</Button></Drawer.Footer>
					</Drawer.Dialog>
				</Drawer.Content>
			</Drawer.Backdrop>

			{(desk.toast || ticketToast) && (
				<div className="toast-dock md:w-72" role="status">
					<Alert status={ticketToast || desk.toast?.ok ? "success" : "danger"}>
						<Alert.Indicator />
						<Alert.Content><Alert.Title>{ticketToast ?? desk.toast?.msg}</Alert.Title></Alert.Content>
					</Alert>
				</div>
			)}
		</div>
	);
}

// ─── the one answer ──────────────────────────────────────────────────────────

function AnswerHero({
	loading, coinCount, error, onRetry, ready, liveSignals, closest, countdown, onOpen,
}: {
	loading: boolean; coinCount: number; error: string | null; onRetry: () => void;
	ready: DeskRow[]; liveSignals: OpenSignal[]; closest: DeskRow | null; countdown: string | null;
	onOpen: (symbol: string) => void;
}) {
	if (loading) {
		return (
			<section aria-busy="true" aria-live="polite" className="rounded-card border border-border bg-surface p-6 sm:p-7">
				<p className="text-xs font-semibold uppercase tracking-wide text-muted">Right now</p>
				<h1 className="mt-1 flex items-center gap-2 text-2xl font-bold leading-tight tracking-tight lg:text-[1.75rem]">
					<RefreshCw className="size-5 shrink-0 animate-spin text-muted" aria-hidden />
					Checking today's coins
				</h1>
				<p className="mt-2 max-w-xl text-sm text-muted">
					{coinCount > 0
						? `${coinCount} coins picked today for day trading: the most traded ones that move enough to pay for fees. Each is checked against every rule on the last closed 1-hour candle.`
						: "Picking today's busiest coins for day trading…"}
				</p>
				<div className="mt-4 flex flex-wrap gap-3">
					<Skeleton className="h-9 w-44 rounded-full" />
					<Skeleton className="h-9 w-56 rounded-full" />
				</div>
			</section>
		);
	}
	if (error) {
		return (
			<Alert status="danger">
				<Alert.Indicator />
				<Alert.Content>
					<Alert.Title>Couldn't check the market</Alert.Title>
					<Alert.Description>{error}</Alert.Description>
				</Alert.Content>
				<Button size="sm" variant="secondary" onPress={onRetry}>Try again</Button>
			</Alert>
		);
	}

	const trades = [
		...ready.map(r => ({ symbol: r.symbol, action: r.signal!.action })),
		...liveSignals.map(o => ({ symbol: o.symbol, action: o.action })),
	];
	const hasTrade = trades.length > 0;
	return (
		<section
			data-tour="answer"
			aria-live="polite"
			className={cn(
				"rounded-card p-6 sm:p-7",
				hasTrade ? "hero-ready text-on-identity" : "border border-border bg-surface",
			)}
		>
			<p className={cn("text-xs font-semibold uppercase tracking-wide", hasTrade ? "text-on-identity/70" : "text-muted")}>
				Right now
			</p>
			<h1 className="mt-1 text-2xl font-bold leading-tight tracking-tight lg:text-[1.75rem]">
				{hasTrade ? `${trades.length} trade${trades.length > 1 ? "s" : ""} ready` : "No trade right now"}
			</h1>

			{hasTrade ? (
				<>
					<p className="mt-2 max-w-xl text-sm text-on-identity/85">
						Every rule passed on the last 1-hour candle. Open the plan and follow the steps exactly.
					</p>
					<div className="mt-4 flex flex-wrap gap-2">
						{trades.map(t => (
							// Lime on the dark banner, so it reads as the one thing to press.
							<Button key={t.symbol} variant="primary" className="bg-highlight text-on-highlight" onPress={() => onOpen(t.symbol)}>
								See {t.symbol.replace("USDT", "")} {t.action === "LONG" ? "buy" : "sell"} plan <ArrowRight className="size-4" />
							</Button>
						))}
					</div>
				</>
			) : (
				<>
					<p className="mt-2 max-w-xl text-sm text-muted">
						That's normal. Good setups only show up a few times a week. Doing nothing is a good trade when nothing is ready.
					</p>
					<div className="mt-4 flex flex-wrap items-center gap-3">
						{countdown && (
							<span className="inline-flex items-center gap-1.5 rounded-full bg-default px-3 py-1.5 text-sm">
								<Clock className="size-4 text-muted" aria-hidden />
								Next check in <span className="num font-semibold">{countdown}</span>
							</span>
						)}
						{closest && (
							<Button variant="outline" size="sm" onPress={() => onOpen(closest.symbol)}>
								Closest: {closest.symbol.replace("USDT", "")}, almost ready <ArrowRight className="size-3.5" />
							</Button>
						)}
					</div>
				</>
			)}

			<p className={cn("mt-5 text-xs", hasTrade ? "text-on-identity/70" : "text-muted")}>
				Honest note: this strategy lost money over the last 90 days of testing. Practise with small amounts.{" "}
				<Link to="/playbook#timeframes" className="underline underline-offset-2">See the numbers</Link>
			</p>
		</section>
	);
}

// ─── coin cards ──────────────────────────────────────────────────────────────

/** While the scan runs: one of today's coins, why it was picked, and a placeholder for its answer. */
function PendingCoinCard({ coin }: { coin: Universe["included"][number] }) {
	const name = coin.symbol.replace("USDT", "");
	return (
		<div className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4" aria-label={`${name}: checking the rules`}>
			<div className="flex items-center gap-3">
				<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-default text-xs font-bold">{name.slice(0, 4)}</span>
				<div className="min-w-0 flex-1">
					<div className="font-semibold">{name}</div>
					<div className="truncate text-xs text-muted"><span className="num">{volumeText(coin.volumeM)}</span> traded in 24h</div>
				</div>
				<Skeleton className="h-5 w-12 shrink-0 rounded-full" />
			</div>
			<p className="text-sm text-muted">Moves about <span className="num">{coin.atrPct.toFixed(1)}%</span> an hour · checking the rules…</p>
			<Skeleton className="mt-auto h-1.5 w-full rounded-full" />
		</div>
	);
}

function CoinCard({ row, onOpen }: { row: DeskRow; onOpen: () => void }) {
	const v = row.verdict;
	const { met, total } = ruleCounts(row);
	const coin = row.symbol.replace("USDT", "");
	// An open Telegram signal is this coin's answer (backend verdictFromOpenSignal).
	const sig = !row.signal ? row.openSignal ?? null : null;
	return (
		<button
			type="button"
			onClick={onOpen}
			className={cn(
				"group flex flex-col gap-3 rounded-card border bg-surface p-4 text-left outline-none transition-colors",
				"hover:border-line-strong focus-visible:ring-2 focus-visible:ring-focus",
				v.status === "enter" ? "border-accent" : "border-border",
			)}
		>
			<div className="flex items-center gap-3">
				<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-default text-xs font-bold">{coin.slice(0, 4)}</span>
				<div className="min-w-0 flex-1">
					<div className="font-semibold">{coin}</div>
					<div className="num text-xs text-muted">${fmt(row.price)}</div>
				</div>
				{sig
					? <Chip size="sm" variant="soft" color={OPEN_SIGNAL_META[sig.state].color} className="font-semibold">{OPEN_SIGNAL_META[sig.state].label}</Chip>
					: <StageChip stage={v.stage} />}
			</div>
			<p className={cn("text-sm font-medium leading-snug", VERDICT_TONE[v.status])}>{v.headline}</p>
			{(row.signal?.againstBtc || sig?.againstBtc) && (
				<p className="-mt-1.5 text-xs font-medium text-warning">Against BTC: half your usual risk</p>
			)}
			{sig ? (
				<p className="num mt-auto flex items-center justify-between gap-2 text-2xs text-muted">
					<span>
						<DirectionChip direction={sig.action} /> Telegram {ago(sig.sentAt)} · entry ${fmt(sig.plan.entry)}
					</span>
					<span className="inline-flex items-center gap-0.5 text-brand-text opacity-0 transition-opacity group-hover:opacity-100">
						Details <ArrowRight className="size-3" />
					</span>
				</p>
			) : (
			<ProgressBar
				aria-label={`${met} of ${total} rules passed`}
				value={(met / total) * 100}
				size="sm"
				color={STAGE_META[v.stage].color}
			>
				<div className="flex justify-between text-2xs text-muted">
					<span>{met} of {total} rules passed</span>
					<span className="inline-flex items-center gap-0.5 text-brand-text opacity-0 transition-opacity group-hover:opacity-100">
						Details <ArrowRight className="size-3" />
					</span>
				</div>
				<ProgressBar.Track><ProgressBar.Fill /></ProgressBar.Track>
			</ProgressBar>
			)}
		</button>
	);
}

// ─── coin drawer: plan or explanation ────────────────────────────────────────

function CoinDrawer({ row, sig, onClose, desk, myTrades, onTrack }: {
	row: DeskRow | null; sig: OpenSignal | null; onClose: () => void; desk: ReturnType<typeof useDesk>;
	myTrades: ChartSignal[]; onTrack: (req: TicketRequest) => void;
}) {
	const [, setMode] = useDeskMode();
	const symbol = row?.symbol ?? sig?.symbol ?? null;
	return (
		<Drawer.Backdrop isOpen={symbol != null} onOpenChange={open => { if (!open) onClose(); }}>
			<Drawer.Content placement="right">
				{/* Width goes on the dialog: the content layer spans the viewport. */}
				<Drawer.Dialog className="w-full sm:w-[30rem]">
					<Drawer.CloseTrigger />
					{symbol && (
						<>
							<Drawer.Header>
								<Drawer.Heading className="flex items-center gap-2">
									{symbol.replace("USDT", "")}
									<span className="num text-sm font-normal text-muted">${fmt(row?.price ?? sig?.price)}</span>
									{row?.signal || !sig
										? row && <StageChip stage={row.verdict.stage} />
										: <Chip size="sm" variant="soft" color={OPEN_SIGNAL_META[sig.state].color} className="font-semibold">{OPEN_SIGNAL_META[sig.state].label}</Chip>}
									{(row?.signal ?? sig)?.againstBtc && <AgainstBtcChip action={(row?.signal ?? sig)!.action} />}
								</Drawer.Heading>
							</Drawer.Header>
							<Drawer.Body className="space-y-5">
								{/* Precedence: a fresh signal on this candle, then the signal already
								    sent to Telegram, then the plain explanation of what's missing. */}
								{row?.signal ? (
									<TradePlanSteps
										symbol={row.symbol} action={row.signal.action} plan={row.signal.plan}
										status={<VerdictBox verdict={row.verdict} />}
										onLog={() => desk.logTrade(row)} logging={desk.logging === row.symbol}
									/>
								) : sig ? (
									<TradePlanSteps
										symbol={sig.symbol} action={sig.action} plan={sig.plan}
										status={<OpenSignalBox sig={sig} />}
										onLog={sig.state === "stopped" ? undefined : () => desk.logOpenSignal(sig)}
										logging={desk.logging === sig.symbol}
										logLabel={sig.state === "valid" ? undefined : "I already placed this trade, log it"}
									/>
								) : row ? (
									<Explanation row={row} />
								) : null}
								<div>
									<div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">1-hour chart</div>
									<p className="mb-2 text-xs text-muted">
										BUY and SELL tags show past signals. Red box: the risk to the stop-loss. Green box: the room to the targets. Tap a tag to see how it ended. A shaded band on the right is the zone this coin is waiting for.
									</p>
									<PriceChart symbol={symbol} timeframe="1h" height={240} allowExpand={false} showSignals deskCheck={deskCheckFor(row)} setup={deskSetupFor(row)} myTrades={myTrades} />
								</div>
							</Drawer.Body>
							<Drawer.Footer>
								<Button variant="outline" size="sm" onPress={() => onTrack({ symbol, side: row?.checklist.direction ?? sig?.action ?? "LONG" })}>
									Track a trade
								</Button>
								<Button variant="ghost" size="sm" onPress={() => { setMode("pro"); onClose(); }}>
									Open in Pro view
								</Button>
								<Button slot="close" variant="secondary">Close</Button>
							</Drawer.Footer>
						</>
					)}
				</Drawer.Dialog>
			</Drawer.Content>
		</Drawer.Backdrop>
	);
}

function Explanation({ row }: { row: DeskRow }) {
	const c = row.checklist;
	const { met, total } = ruleCounts(row);
	const engine = row.verdict.engineCheck;
	return (
		<>
			<VerdictBox verdict={row.verdict} />
			<ClosePreviewFor symbol={row.symbol} armed={row.verdict.stage === "armed"} />
			<div>
				<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
					What we check for a {c.direction === "LONG" ? "buy" : "sell"} ({met} of {total} passed)
				</div>
				<ul className="space-y-3">
					{c.items.map(it => (
						<li key={it.key} className="flex gap-2.5">
							<StatusIcon status={it.status} warn={it.warn} />
							<div className="min-w-0">
								<p className={cn("text-sm leading-snug", it.warn && "text-warning")}>{it.plain || it.label}<RuleHelp ruleKey={it.key} label={it.label} /></p>
								<p className="num mt-0.5 text-xs text-muted">Now: {it.detail}</p>
							</div>
						</li>
					))}
					{engine && (
						// The engine's own rule that failed: listed last, like the others,
						// so a blocked coin never reads "all rules passed".
						<li className="flex gap-2.5 rounded-lg bg-danger-soft p-2">
							<StatusIcon status="failed" />
							<div className="min-w-0">
								<p className="text-sm font-medium leading-snug">{engine.label}</p>
								<p className="text-sm leading-snug text-foreground/80">{engine.plain}</p>
								<p className="num mt-0.5 text-xs text-muted">Now: {engine.detail}</p>
							</div>
						</li>
					)}
				</ul>
			</div>
		</>
	);
}

/** The trade as numbered steps in beginner words, with the numbers large. */
function TradePlanSteps({ symbol, action, plan: p, status, onLog, logging, logLabel }: {
	symbol: string; action: "LONG" | "SHORT"; plan: Plan; status: ReactNode;
	onLog?: () => void; logging: boolean; logLabel?: string;
}) {
	const coin = symbol.replace("USDT", "");
	const long = action === "LONG";
	// Drawdown brake engaged: every size is multiplied by 0. Say so plainly
	// instead of showing "Size 0.000000 (≈ $0)" and "you lose about $0.00".
	const paused = p.position.drawdownMultiplier === 0;
	const steps: { title: string; value: string; note: string; tone?: "profit" | "loss" }[] = [
		{
			title: long ? "Buy now, at about" : "Sell (short) now, at about",
			value: `$${fmt(p.entry)}`,
			note: paused
				? "Size: none. Trading is paused because your account is down too much (the drawdown brake). Don't trade until it's lifted in Settings."
				: `Size: ${fmt(p.position.units)} ${coin} (≈ $${p.position.notional.toLocaleString()}). If price already moved far past this, skip it. Don't chase.`,
		},
		{
			title: "Straight away, set your stop-loss at",
			value: `$${fmt(p.stopLoss)}`,
			note: paused
				? "If price reaches it the trade closes. That's where the idea is proven wrong."
				: `If price reaches it the trade closes and you lose about $${p.position.riskAmount.toFixed(2)}. That's the most you can lose.`,
			tone: "loss",
		},
		{
			title: "Take half your profit at",
			value: `$${fmt(p.tp1)}`,
			note: "Then move your stop-loss up to your entry price, so the rest can't lose money.",
			tone: "profit",
		},
		{
			title: "Close the rest at",
			value: `$${fmt(p.tp2)}`,
			note: `If nothing happens within ${p.timeStopHours} hours, close the trade anyway.`,
			tone: "profit",
		},
	];
	return (
		<div className="space-y-4">
			{status}
			<ol className="space-y-3">
				{steps.map((st, i) => (
					<li key={i} className="flex gap-3 rounded-card border border-border bg-surface-secondary p-3">
						<span className="num flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-on-brand">{i + 1}</span>
						<div className="min-w-0">
							<p className="text-sm text-muted">{st.title}</p>
							<p className={cn("num text-xl font-semibold", st.tone === "loss" ? "text-loss" : st.tone === "profit" ? "text-profit" : "text-foreground")}>{st.value}</p>
							<p className="mt-0.5 text-xs leading-relaxed text-muted">{st.note}</p>
						</div>
					</li>
				))}
			</ol>
			{onLog && (
				<Button variant="primary" fullWidth onPress={onLog} isPending={logging}>
					{logLabel ?? "I placed this trade, log it"}
				</Button>
			)}
		</div>
	);
}

// ─── signals already sent to Telegram ────────────────────────────────────────

const OPEN_ALERT: Record<OpenSignal["state"], "success" | "accent" | "warning" | "default" | "danger"> = {
	valid: "success", "in-profit": "accent", missed: "warning", expired: "default", stopped: "danger",
};

/** What a Telegram signal means right now, for someone not yet in the trade. */
function OpenSignalBox({ sig }: { sig: OpenSignal }) {
	return (
		<Alert status={OPEN_ALERT[sig.state]}>
			<Alert.Indicator />
			<Alert.Content>
				<Alert.Title>{sig.headline}</Alert.Title>
				<Alert.Description className="space-y-1">
					<span className="block">Sent to Telegram {ago(sig.sentAt)}. {sig.explanation}</span>
					<span className="block font-medium">What to do: {sig.next}</span>
				</Alert.Description>
			</Alert.Content>
		</Alert>
	);
}

function SignalCard({ sig, onOpen }: { sig: OpenSignal; onOpen: () => void }) {
	const meta = OPEN_SIGNAL_META[sig.state];
	return (
		<button
			type="button"
			onClick={onOpen}
			className={cn(
				"flex flex-col gap-2 rounded-card border bg-surface p-4 text-left outline-none transition-colors",
				"hover:border-line-strong focus-visible:ring-2 focus-visible:ring-focus",
				sig.state === "valid" ? "border-accent" : "border-border",
			)}
		>
			<div className="flex items-center gap-2">
				<span className="font-semibold">{sig.symbol.replace("USDT", "")}</span>
				<DirectionChip direction={sig.action} />
				{sig.againstBtc && <AgainstBtcChip action={sig.action} />}
				<Chip size="sm" variant="soft" color={meta.color} className="ml-auto font-semibold">{meta.label}</Chip>
			</div>
			<p className="text-sm font-medium">{sig.headline}</p>
			<p className="num text-xs text-muted">
				Sent {ago(sig.sentAt)} · entry ${fmt(sig.plan.entry)} · now ${fmt(sig.price)}
			</p>
		</button>
	);
}

// ─── right column ────────────────────────────────────────────────────────────

/** Daily loss limit in plain words (reads /api/day-risk/status). */
function SafetyCard() {
	const [status, setStatus] = useState<DailyRiskStatus | null>(null);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			try {
				const res = await fetch("/api/day-risk/status");
				const json = await res.json();
				if (!cancelled) {
					if (json.success) { setStatus(json.status); setFailed(false); } else setFailed(true);
				}
			} catch {
				if (!cancelled) setFailed(true);
			}
		};
		load();
		const id = setInterval(load, 30_000);
		return () => { cancelled = true; clearInterval(id); };
	}, []);

	const usd = (r: number) => (status ? `$${(r * status.riskPerTradeUsd).toFixed(2)}` : "");
	return (
		<section data-tour="safety" className="rounded-card border border-border bg-surface p-5">
			<div className="flex items-center gap-2">
				<ShieldCheck className="size-4 text-brand-text" aria-hidden />
				<h2 className="text-sm font-semibold">Today's safety limit</h2>
			</div>
			{!status ? (
				failed ? <p className="mt-2 text-sm text-muted">Couldn't load your limits.</p> : <Skeleton className="mt-3 h-20 rounded-lg" />
			) : status.locked ? (
				<Alert status="danger" className="mt-3">
					<Alert.Indicator />
					<Alert.Content>
						<Alert.Title>Stop for today</Alert.Title>
						<Alert.Description>{status.reason ?? "You've reached today's limit."} It resets at midnight UTC. Any real trade you log before then is marked as breaking the limit.</Alert.Description>
					</Alert.Content>
				</Alert>
			) : (
				<>
					<p className="mt-2 text-sm">
						You can still lose up to <span className="num font-semibold">{usd(status.remainingR)}</span> today
						and take <span className="num font-semibold">{status.remainingTrades}</span> more trade{status.remainingTrades === 1 ? "" : "s"}.
					</p>
					<ProgressBar
						className="mt-3"
						aria-label="Daily loss limit used"
						value={Math.min(100, ((status.config.maxDailyLossR - status.remainingR) / status.config.maxDailyLossR) * 100)}
						size="sm"
						color="warning"
					>
						<ProgressBar.Track><ProgressBar.Fill /></ProgressBar.Track>
					</ProgressBar>
					<p className="mt-2 text-xs text-muted">
						Each trade risks {usd(1)} ({status.config.riskPerTradePct}% of ${status.config.accountSize}). When the limit is used up, the desk tells you to stop.
					</p>
				</>
			)}
			<Link to="/settings" className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "mt-2 -ml-2")}>Change limits</Link>
		</section>
	);
}

const LEARN = [
	{ title: "What is a stop-loss?", body: "The price where you admit the trade was wrong and get out small.", to: "/playbook#managing", mins: 1 },
	{ title: "Why wait for the 1-hour close?", body: "A candle can change its mind until it closes. We only act on finished candles.", to: "/playbook#funnel", mins: 2 },
	{ title: "How do I read the chart?", body: "What each line means, and exactly when to buy and when to sell.", to: "/playbook#chart", mins: 4 },
	{ title: "How much should I risk?", body: "0.5% of your account per trade (never more than 1%), so a losing streak can't wipe you out.", to: "/playbook#risk", mins: 2 },
];

/** Bite-size explainers (Coinbase / PayPal "Learn" cards). */
function LearnCards() {
	return (
		<section aria-labelledby="learn-title" className="rounded-card border border-border bg-surface p-5">
			<div className="flex items-center gap-2">
				<BookOpen className="size-4 text-brand-text" aria-hidden />
				<h2 id="learn-title" className="text-sm font-semibold">Learn the basics</h2>
			</div>
			<ul className="mt-3 space-y-1">
				{LEARN.map(l => (
					<li key={l.title}>
						<Link to={l.to} className="-mx-2 block rounded-lg px-2 py-2 outline-none transition-colors hover:bg-accent-tint focus-visible:ring-2 focus-visible:ring-focus">
							<div className="flex items-center justify-between gap-2">
								<span className="text-sm font-medium">{l.title}</span>
								<span className="shrink-0 text-2xs text-muted">{l.mins} min read</span>
							</div>
							<p className="text-xs text-muted">{l.body}</p>
						</Link>
					</li>
				))}
			</ul>
			<p className="mt-3 flex items-start gap-1.5 text-xs text-muted">
				<Sparkles className="mt-0.5 size-3.5 shrink-0" aria-hidden />
				<span>New here? Press <span className="font-semibold">?</span> in the top bar for a 1-minute tour.</span>
			</p>
		</section>
	);
}
